import type { AxiosResponse } from 'axios';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import moment from 'moment';
import { EventType, createEventBusPublisher } from '../react/hooks/useEventBus';

const ENDPOINT = '';
const timezoneOffset = moment().utcOffset().toString();

axiosRetry(axios, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
});

const publishUnauthorizedResponse = createEventBusPublisher(EventType.UnauthorizedApiResponse);

const searchParams = new URLSearchParams(window.location.search);
const API_ENDPOINTS_ALLOWING_TOKEN = ['/api/praise/wall', '/api/praise/wall/config', '/api/v2/users/medium/by-logins'];
axios.interceptors.request.use(
    config => {
        if (searchParams.has('token') && config.url && API_ENDPOINTS_ALLOWING_TOKEN.includes(config.url)) {
            config.params = config.params || {};

            // Add the token to the request's query parameters if it's not already there
            if (!config.params.token) {
                config.params.token = searchParams.get('token');
            }
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

axios.interceptors.response.use(
    (response: AxiosResponse<any>) => {
        return response;
    },
    (error: any) => {
        if (error?.response?.status === 401) {
            publishUnauthorizedResponse(error?.response);
        }
        return Promise.reject(error);
    }
);

export function get<T>(resource: string, { getData = {}, headers = {} }: RequestArgs = {}, urlParams = '') {
    return doRequest<T>(resource, { getData, headers }, 'get', urlParams);
}

export function post<T>(
    resource: string,
    { postData = {}, getData = {}, headers = {} }: RequestArgs = {},
    urlParams = ''
) {
    return doRequest<T>(resource, { postData, getData, headers }, 'post', urlParams);
}

export function put<T>(
    resource: string,
    { postData = {}, getData = {}, headers = {} }: RequestArgs = {},
    urlParams = ''
) {
    return doRequest<T>(resource, { postData, getData, headers }, 'put', urlParams);
}

export function patch<T>(
    resource: string,
    { postData = {}, getData = {}, headers = {} }: RequestArgs = {},
    urlParams = ''
) {
    return doRequest<T>(resource, { postData, getData, headers }, 'patch', urlParams);
}

export function remove<T = void>(resource: string, { getData = {}, headers = {} }: RequestArgs = {}, urlParams = '') {
    return doRequest<T>(resource, { getData, headers }, 'delete', urlParams);
}

type RequestArgs = {
    postData?: any;
    getData?: GetData;
    headers?: { [header: string]: string };
};

function doRequest<T>(
    resource: string,
    { postData, getData, headers = {} }: RequestArgs = {},
    verb: 'get' | 'post' | 'put' | 'patch' | 'delete',
    urlParams: string
): Promise<T> {
    if (!headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    if (!headers['X-Timezone-Offset']) {
        headers['X-Timezone-Offset'] = timezoneOffset;
    }

    switch (verb) {
        case 'get':
            return axios.get<T>(getUrl(resource, urlParams), { headers, params: getData }).then(r => r.data);
        case 'post':
            return axios.post<T>(getUrl(resource, urlParams), postData, { headers, params: getData }).then(r => r.data);
        case 'put':
            return axios.put<T>(getUrl(resource, urlParams), postData, { headers, params: getData }).then(r => r.data);
        case 'patch':
            return axios
                .patch<T>(getUrl(resource, urlParams), postData, { headers, params: getData })
                .then(r => r.data);
        case 'delete':
            // The types for axios are just wrong. Delete does return the payload
            return axios
                .delete(getUrl(resource, urlParams), { headers, params: getData })
                .then(r => r.data) as Promise<T>;
        default:
            // @ts-ignore This was the original implementation. It's unsound, but somehow still worked?
            return axios[verb](getUrl(resource, urlParams), postData, { headers, params: getData }).then(r => r.data);
    }
}

function getUrl(resource: string, urlParams: string, getData?: GetData) {
    const getDataFormatted = getData ? formatGetData(getData) : '';
    return withEndpoint([resource, urlParams].filter(x => x.length > 0).join('/') + getDataFormatted);
}

function withEndpoint(url: string) {
    return ENDPOINT + url;
}

type GetData = { [id: string]: string | undefined | boolean };

function formatGetData(getData: GetData): string {
    const keys = Object.keys(getData);
    if (keys.length === 0) {
        return '';
    } else {
        return (
            '?' +
            keys
                .reduce<string[]>((mem, id: keyof typeof getData) => {
                    if (getData[id] !== undefined) {
                        mem.push(id + '=' + getData[id]);
                    }
                    return mem;
                }, [] as string[])
                .join('&')
        );
    }
}
